import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// VehicleSchematicSnapshot.jsx
//
// Snapshot estático del esquemático del vehículo enfocado en una alarma —
// reutiliza el MISMO algoritmo de layout (parse de `figure`) y el MISMO
// componente atómico (PositionTile) que el panel de control, pero sin la
// lógica viva: no consulta tickets en tiempo real, no escucha websocket,
// no muestra receiver dot ni chips, no permite click para abrir telemetría.
//
// Recibe:
//   - vehicle: { id, alias, figure, positions_count }
//   - highlightPositions: number[] — posiciones que están en alarma.
//   - groupPositions: number[] — posiciones que forman el grupo Dual
//       (incluye highlightPositions).
//   - alarmType: string — 'dual_yoyo' | 'pressure_low_alarm' | etc.
//   - metricType: 'PSI' | 'TEMP' (default 'PSI')
//   - dense: boolean — versión más compacta.
//
// Para pintar las posiciones afectadas, sintetiza `alarmStatuses` y
// `positionTickets` con el mismo shape que el panel de control, de modo
// que PositionTile aplica exactamente los mismos estilos (borde rojo,
// halo, marca Dual) que en el dashboard real.

import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  DirectionsCar as VehicleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

import PositionTile from './PositionTile';

// Wrapper con tamaño consistente, idéntico al usado en VehicleSchematicCard.
const StyledPositionTile = (props) => (
  <Box sx={{
    width: { xs: 32, sm: 38 },
    height: { xs: 32, sm: 38 },
    flexShrink: 0,
    flexGrow: 0
  }}>
    <PositionTile {...props} />
  </Box>
);

// Parser de figure → array de ejes (filas). Misma lógica que VehicleSchematicCard.
const parseFigureToLayout = (vehicle) => {
  if (!vehicle || !vehicle.positions_count) return [];

  if (!vehicle.figure) {
    const positions = Array.from({ length: vehicle.positions_count }, (_, i) => i + 1);
    if (positions.length <= 4) return [positions];
    const rows = [];
    for (let i = 0; i < positions.length; i += 4) {
      rows.push(positions.slice(i, i + 4));
    }
    return rows;
  }

  const axles = vehicle.figure.split(',').map((a) => a.trim());
  const computed = [];
  let counter = 1;
  axles.forEach((axleConfig) => {
    const row = [];
    if (axleConfig.includes('-')) {
      const [left, right] = axleConfig.split('-').map((n) => parseInt(n.trim(), 10));
      for (let i = 0; i < left; i++) {
        if (counter <= vehicle.positions_count) row.push(counter++);
      }
      for (let i = 0; i < right; i++) {
        if (counter <= vehicle.positions_count) row.push(counter++);
      }
    } else {
      const tires = parseInt(axleConfig, 10);
      for (let i = 0; i < tires; i++) {
        if (counter <= vehicle.positions_count) row.push(counter++);
      }
    }
    if (row.length > 0) computed.push(row);
  });

  if (counter <= vehicle.positions_count) {
    const remaining = [];
    while (counter <= vehicle.positions_count) remaining.push(counter++);
    if (remaining.length > 0) computed.push(remaining);
  }
  return computed;
};

const VehicleSchematicSnapshot = ({
  vehicle,
  highlightPositions = [],
  groupPositions = [],
  alarmType = null,
  metricType = 'PSI',
  dense = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const layout = useMemo(() => parseFigureToLayout(vehicle), [vehicle?.figure, vehicle?.positions_count]);

  const highlightSet = useMemo(
    () => new Set((highlightPositions || []).map(Number).filter(Number.isFinite)),
    [highlightPositions]
  );
  const groupArr = useMemo(
    () => (groupPositions || []).map(Number).filter(Number.isFinite),
    [groupPositions]
  );
  const isDualYoyo = String(alarmType || '').toLowerCase() === 'dual_yoyo';

  // Casos límite tempranos.
  if (!vehicle) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">{t('hardcoded.components_schematics_ticketschematicview.vehiculo_no_disponible')}</Typography>
      </Box>
    );
  }
  if (layout.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <VehicleIcon sx={{ fontSize: 32, color: 'text.disabled' }} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('hardcoded.components_schematics_ticketschematicview.posicion_no_disponible')}</Typography>
      </Box>
    );
  }

  // Para una posición resaltada, sintetizamos alarmStatus + positionTicket que
  // PositionTile reconoce y pinta igual que en el panel.
  const synthesizeForPosition = (pos) => {
    const inHighlight = highlightSet.has(Number(pos));
    if (!inHighlight) return { alarmStatuses: [], positionTickets: [] };

    const alarmStatuses = [{
      vehicle_id: vehicle.id,
      position: Number(pos),
      status: 'CRITICAL',
      metric_type: metricType,
      alarm_reason: alarmType || 'threshold_breach',
      active_reasons: isDualYoyo ? [{
        alarm_reason: 'dual_yoyo',
        group_positions: groupArr,
      }] : [],
      group_positions: isDualYoyo ? groupArr : undefined,
    }];

    const positionTickets = [{
      vehicle_id: vehicle.id,
      position: Number(pos),
      status: 'open',
      metric_type: metricType,
      alarm_reason: alarmType || 'threshold_breach',
      group_positions: isDualYoyo ? groupArr : undefined,
    }];

    return { alarmStatuses, positionTickets };
  };

  // Detecta pares Dual adyacentes en cada fila para pintar el borde
  // común exterior — copia de la misma lógica del panel.
  const isDualPair = (a, b) => isDualYoyo && a != null && b != null
    && groupArr.includes(Number(a)) && groupArr.includes(Number(b));

  return (
    <Box sx={{ width: '100%' }}>
      {/* Cabecera ligera para identificar el vehículo */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.75 }}>
        <VehicleIcon sx={{ fontSize: dense ? 16 : 20, color: 'text.secondary' }} />
        <Typography variant={dense ? 'body2' : 'subtitle2'} fontWeight={700} noWrap>
          {vehicle.alias || t('hardcoded.components_schematics_vehicleschematicsnapshot.vehiculo_stringvehicleid', { Stringvehicleid: String(vehicle.id).slice(0, 8) })}
        </Typography>
        {isDualYoyo && (
          <Chip
            size="small"
            color="error"
            label={t('hardcoded.components_alarmprofiles_profileform.dual')}
            icon={<WarningIcon sx={{ fontSize: 12 }} />}
            sx={{ height: 20, fontSize: 10 }}
          />
        )}
      </Box>

      {/* Grid de posiciones — mismo layout que VehicleSchematicCard */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: { xs: 0.6, sm: 0.8 },
        py: 0.5,
        width: '100%',
        px: { xs: 0.3, sm: 0.5 },
      }}>
        {layout.map((row, rowIndex) => (
          <Box
            key={`snap-row-${rowIndex}`}
            sx={{
              display: 'flex',
              gap: { xs: 0.25, sm: 0.3, md: 0.4 },
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'nowrap',
              width: 'fit-content',
              maxWidth: '100%',
              ...(row.length === 1 && { justifyContent: 'center' }),
              ...(row.length === 2 && { width: { xs: '100px', sm: '120px' }, justifyContent: 'space-between' }),
              ...(row.length === 3 && { width: { xs: '120px', sm: '140px' }, justifyContent: 'space-between' }),
              ...(row.length === 4 && { width: { xs: '140px', sm: '160px' }, justifyContent: 'space-between' }),
              ...(row.length > 4 && { width: { xs: '180px', sm: '200px' }, justifyContent: 'space-evenly', flexWrap: 'wrap' }),
            }}
          >
            {(() => {
              const out = [];
              let i = 0;
              while (i < row.length) {
                const pos = row[i];
                const next = row[i + 1];
                const pairWithNext = isDualPair(pos, next);

                const tileFor = (p, suppressOutline) => {
                  const { alarmStatuses, positionTickets } = synthesizeForPosition(p);
                  return (
                    <StyledPositionTile
                      key={`snap-pos-${vehicle.id}-${p}`}
                      vehicle={vehicle}
                      position={p}
                      realtimeData={undefined}
                      lastAvailableData={undefined}
                      alarms={[]}
                      alarmStatuses={alarmStatuses}
                      positionTickets={positionTickets}
                      onTelemetryClick={() => { /* snapshot: no abre popups */ }}
                      displayMode={metricType === 'TEMP' ? 'temperature' : 'pressure'}
                      suppressYoyoOutline={suppressOutline}
                      staticSnapshot
                    />
                  );
                };

                if (pairWithNext) {
                  // Wrapper común con borde exterior — mismo estilo que el card real.
                  out.push(
                    <Box
                      key={`snap-yoyo-pair-${pos}-${next}`}
                      sx={{
                        display: 'inline-flex',
                        gap: 0.25,
                        padding: '4px',
                        borderRadius: 1,
                        boxShadow: (tt) => `inset 0 0 0 1px ${tt.palette.common.black}, inset 0 0 0 4px ${tt.palette.error.main}`,
                      }}
                    >
                      {tileFor(pos, true)}
                      {tileFor(next, true)}
                    </Box>
                  );
                  i += 2;
                } else {
                  out.push(tileFor(pos, false));
                  i += 1;
                }
              }
              return out;
            })()}
          </Box>
        ))}
      </Box>

      {/* Leyenda compacta */}
      {(highlightSet.size > 0 || groupArr.length > 0) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2, mt: 1, justifyContent: 'center' }}>
          {highlightSet.size > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{
                width: 12, height: 12, borderRadius: '50%',
                background: alpha(theme.palette.error.main, 0.25),
                border: `2px solid ${theme.palette.error.main}`,
              }} />
              <Typography variant="caption" color="text.secondary">{t('hardcoded.components_schematics_vehicleschematicsnapshot.origen_de_la_alarma')}</Typography>
            </Box>
          )}
          {isDualYoyo && groupArr.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{
                width: 14, height: 10,
                background: 'transparent',
                border: `2px solid ${theme.palette.error.main}`,
                borderRadius: 0.5,
              }} />
              <Typography variant="caption" color="text.secondary">{i18n.t('hardcoded.components_schematics_ticketschematicview.grupo_dual')}</Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default React.memo(VehicleSchematicSnapshot);
